// Presentation only: these labels never decide whether or how an action runs.
const lookup = (values, key, fallback) => Object.prototype.hasOwnProperty.call(values, key) ? values[key] : fallback;

const STATUS_GROUPS = {
  success: 'done', completed: 'done', failed: 'failed', error: 'failed',
  skipped: 'skipped', cached: 'reused', triggered: 'started',
  running: 'running', pending: 'waiting', queued: 'waiting'
};

export const getLogFileName = (value) => String(value || '').split(/[\\/]/).pop() || '';

export const getActionLogOutcome = (log) => {
  // A successful queue request does not mean the document has been analysed.
  if (String(log.status).trim().toLowerCase() === 'success'
    && /^Queued for reanalysis by \d+$/.test(String(log.success_details || '').trim())) {
    return 'waiting';
  }
  return lookup(STATUS_GROUPS, String(log.status || '').trim().toLowerCase(), 'unknown');
};

const ACTION_LABELS = {
  file_process: ['analyse', 'Document analysis'],
  timeevent: ['eventUpdate', 'Event update'],
  widget: ['widget', 'Dashboard update'],
  import: ['import', 'Data import'],
  scan: ['scan', 'Search for new documents'],
  search: ['search', 'Search'],
  sendemail: ['email', 'Send an email'],
  createproject: ['project', 'Create a project'],
  tag: ['tag', 'Update information']
};

const CONTEXT_LABELS = {
  automatic_retry: ['retry', 'Automatic retry'],
  force_run: ['manual', 'Started by a user'],
  manual_reanalyze: ['manual', 'Requested by a user'],
  bulk_reanalyze_errors: ['manual', 'Requested by a user'],
  automated_schedule: ['scheduled', 'Scheduled automatically'],
  automated_flow_schedule: ['scheduled', 'Scheduled automatically'],
  automated_replay: ['automatic', 'Started automatically'],
  automated_batch: ['automatic', 'Started automatically'],
  file_processor: ['automatic', 'Started automatically'],
  import_manager: ['import', 'During import'],
  confirm_event: ['manual', 'Requested by a user'],
  delete_timeevent: ['manual', 'Requested by a user'],
  update_tag: ['manual', 'Requested by a user'],
  delete_variable: ['manual', 'Requested by a user'],
  update_event_date: ['manual', 'Requested by a user']
};

const getRecordedActionData = (log) => {
  // Read only known recorded input sections, never current settings or result objects.
  const objectFromJson = (value) => {
    try {
      const data = typeof value === 'string' ? JSON.parse(value) : value;
      return data && typeof data === 'object' && !Array.isArray(data) ? data : null;
    } catch {
      return null;
    }
  };
  for (const details of [log.error_details, log.success_details]) {
    if (typeof details !== 'string' || details.length > 12000) continue;
    const section = details.match(/^Action data:\s*(\{[^\r\n]*\})\s*$/m);
    const actionData = section && objectFromJson(section[1]);
    if (actionData) return actionData;
    // Older failure logs end with a multiline JSON Context containing action_data.
    // Extract only that field: target IDs, results and execution/storage data are not inputs.
    const contextSection = details.match(/^Context:\s*\r?\n(\{[\s\S]*\})\s*$/m);
    const context = contextSection && objectFromJson(contextSection[1]);
    const legacyActionData = context && objectFromJson(context.action_data);
    if (legacyActionData) return legacyActionData;
  }
  return null;
};

export const getActionLogRecordedSettings = (log, { limit = 8, includePaths = false } = {}) => {
  const data = getRecordedActionData(log);
  const values = [];
  let partial = false;
  let visits = 0;
  const visit = (value, path, depth) => {
    if (++visits > 150 || depth > 5 || values.length >= limit) { partial = true; return; }
    if (value && typeof value === 'object') {
      Object.entries(value).slice(0, 100).forEach(([key, item]) => {
        const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (key.startsWith('_') || /password|passwd|secret|token|credential|apikey|accesskey|privatekey|authorization|authheader|nonce|cookie|bearer/.test(normalized)
          || normalized === 'auth' || normalized.endsWith('auth') || (!path.length && key === 'description')) return;
        visit(item, [...path, key], depth + 1);
      });
      return;
    }
    if (!['string', 'number', 'boolean'].includes(typeof value) && value !== null) return;
    // Older entries may predate server redaction. Do not promote embedded credentials
    // or serialized objects into a hover preview either.
    if (typeof value === 'string' && (['{', '['].includes(value.trimStart()[0])
      || /\b(?:Bearer|Basic)\s+\S|https?:\/\/[^\s/@]+:[^\s/@]+@|(?:password|secret|token|api[_-]?key|authorization|cookie)\S*["']?\s*[:=]/i.test(value))) return;
    const label = path.map(key => key.replace(/([\p{Ll}\d])(\p{Lu})/gu, '$1 $2').replace(/[_-]+/g, ' ')).join(' · ');
    if (typeof value === 'string' && value.length > 300) partial = true;
    values.push({ label, value: typeof value === 'string' && value.length > 300 ? `${value.slice(0, 300)}…` : value,
      ...(includePaths ? { path } : {}) });
  };
  if (data) visit(data, [], 0);
  return { values, partial };
};

export const getActionLogIdentity = (log, catalogEntry) => {
  const recordedDescription = getRecordedActionData(log)?.description;
  const text = (value) => typeof value === 'string' ? value.trim() : '';
  const matchingEntry = catalogEntry?.type === log.action_type ? catalogEntry : null;
  const name = [log.action_name, log.configured_action_name, matchingEntry?.name]
    .map(text).find(value => value && value !== text(log.action_type)) || '';
  const description = text(log.action_description) || text(recordedDescription) || text(log.configured_action_description) || text(matchingEntry?.description);
  return { name, description };
};

export const getActionLogLabel = (log, t, identity = getActionLogIdentity(log)) => {
  const { name } = identity;
  if (log.action_type === 'retry_scheduled') {
    return t('actionsLogs.activity.actions.retry', 'Automatic retry scheduled');
  }
  if (log.action_id != null && name) return name;
  if (log.action_type === 'file_process') {
    return t('actionsLogs.activity.actions.analyse', 'Document analysis');
  }
  if (['manual_reanalyze', 'bulk_reanalyze_errors'].includes(log.execution_context)
    || log.action_name === 'Manual reanalysis requested') {
    return t('actionsLogs.activity.actions.reanalyse', 'Analyse the event again');
  }
  if (log.action_type === 'timeevent') {
    const eventActions = {
      confirm_event: ['confirm', 'Confirm the event'],
      update_tag: ['tag', 'Update information'],
      delete_variable: ['removeInformation', 'Remove information'],
      update_event_date: ['date', 'Change the event date'],
      delete_timeevent: log.action_name === 'Unlinked linked project'
        ? ['unlink', 'Unlink the project'] : ['removeEvent', 'Remove the event']
    };
    const label = lookup(eventActions, log.execution_context, null);
    if (label) return t(`actionsLogs.activity.actions.${label[0]}`, label[1]);
  }
  if (name) return name;
  const [key, fallback] = lookup(ACTION_LABELS, log.action_type, ['other', 'Action']);
  return t(`actionsLogs.activity.actions.${key}`, fallback);
};

export const getActionLogOriginInfo = (context, t) => {
  if (/^loop_trigger_flow_item_\d+$/.test(String(context || ''))) {
    return { kind: 'repeated', label: t('actionsLogs.activity.origins.repeated', 'Repeated automatically') };
  }
  const [key, fallback] = lookup(CONTEXT_LABELS, context, ['unknown', 'Origin not recorded']);
  return { kind: key, label: t(`actionsLogs.activity.origins.${key}`, fallback) };
};

export const getActionLogOrigin = (context, t) => getActionLogOriginInfo(context, t).label;

const readActionSkipConditionReason = (log) => {
  const details = [log.error_details, log.success_details]
    .filter(value => typeof value === 'string')
    .join('\n');
  const match = details.match(/ACTION_SKIP_REASON:(\{[^\r\n]*\})/);
  if (!match) return null;
  try {
    const reason = JSON.parse(match[1]);
    if (reason?.version !== 1 || reason?.code !== 'conditions_not_met'
      || !Array.isArray(reason.failed_conditions)) return null;
    const failedConditions = reason.failed_conditions.slice(0, 10).filter(condition =>
      condition && typeof condition.tag === 'string' && condition.tag.trim()
      && typeof condition.operator === 'string'
    ).map(condition => ({
      tag: condition.tag.trim().slice(0, 160),
      operator: condition.operator.trim().toLowerCase().slice(0, 40),
      expected: typeof condition.expected === 'string' ? condition.expected.slice(0, 200) : ''
    }));
    if (!failedConditions.length) return null;
    const failedConditionCount = Number.isSafeInteger(reason.failed_condition_count)
      ? Math.max(reason.failed_condition_count, failedConditions.length)
      : failedConditions.length;
    return {
      logic: reason.logic === 'OR' ? 'OR' : 'AND',
      failedConditions,
      additionalFailedConditions: Math.max(0, failedConditionCount - failedConditions.length)
    };
  } catch {
    return null;
  }
};

export const getActionLogSkipConditionExplanation = (log, t) => {
  const details = [log.error_details, log.success_details]
    .filter(value => typeof value === 'string')
    .join('\n');
  const missingTags = details.match(/Skipped:\s*missing required condition tag\(s\):\s*([^\r\n]+)/i);
  if (missingTags) {
    return t('actionsLogs.activity.explanations.missingConditionTags',
      'The action was skipped because these required condition tags are missing: {{tags}}.',
      { tags: missingTags[1].trim() });
  }

  const reason = readActionSkipConditionReason(log);
  if (!reason) return null;

  const operatorFallbacks = {
    equals: 'equals', not_equals: 'does not equal', contains: 'contains',
    not_contains: 'does not contain', startswith: 'starts with', endswith: 'ends with',
    match_regex: 'matches the pattern', not_empty: 'is not empty', is_empty: 'is empty',
    more: 'is greater than', less: 'is less than', less_or_equal: 'is less than or equal to',
    file_exists: 'file exists', folder_exists: 'folder exists'
  };
  const valueLessOperators = new Set(['not_empty', 'is_empty']);
  let conditions = reason.failedConditions.map(({ tag, operator, expected }) => {
    const operatorLabel = t(
      `actionsLogs.activity.conditionOperators.${operator}`,
      operatorFallbacks[operator] || 'does not match'
    );
    const comparisonValue = valueLessOperators.has(operator) || !expected ? '' : ` ${expected}`;
    return `${tag} ${operatorLabel}${comparisonValue}`;
  }).join('; ');
  if (reason.additionalFailedConditions) {
    conditions += t('actionsLogs.activity.explanations.conditionSkipMore', '; and {{count}} more', {
      count: reason.additionalFailedConditions
    });
  }

  const explanationKey = reason.logic === 'OR' ? 'conditionSkipOr' : 'conditionSkipAnd';
  const fallback = reason.logic === 'OR'
    ? 'The action was skipped because none of its conditions passed: {{conditions}}.'
    : 'The action was skipped because a required condition did not pass: {{conditions}}.';
  return t(`actionsLogs.activity.explanations.${explanationKey}`, fallback, { conditions });
};

export const getActionLogExplanation = (log, t, processingSummary) => {
  if (log.action_type === 'retry_scheduled') {
    // This contract describes the saved schedule, never the result of the retry.
    try {
      const details = typeof log.success_details === 'string' && log.success_details.length <= 12000
        ? log.success_details : '{}';
      const retry = JSON.parse(details)?.retry;
      if (retry?.version === 1 && ['ingestion', 'analysis', 'action'].includes(retry.kind)
        && Number.isSafeInteger(retry.attempt) && retry.attempt > 0
        && typeof retry.next_attempt_at === 'string'
        && /(?:Z|[+-]\d{2}:\d{2})$/.test(retry.next_attempt_at)) {
        const due = new Date(retry.next_attempt_at);
        if (Number.isFinite(due.getTime())) {
          const kindLabels = { ingestion: 'Document import', analysis: 'Document analysis', action: 'Automatic action' };
          return t('actionsLogs.activity.explanations.retryScheduled', '{{kind}} — retry #{{attempt}} scheduled for {{date, datetime}}.', {
            kind: t(`actionsLogs.activity.retryKinds.${retry.kind}`, kindLabels[retry.kind]),
            attempt: retry.attempt,
            date: due,
            formatParams: { date: { dateStyle: 'short', timeStyle: 'medium' } },
          });
        }
      }
    } catch {
      // An incomplete legacy entry remains readable without inventing a date.
    }
    return t('actionsLogs.activity.explanations.retryScheduledUnknown', 'An automatic retry was scheduled. Its date or attempt number was not recorded.');
  }
  const outcome = getActionLogOutcome(log);
  const rawReason = String(log.error_details || log.success_details || '').trim();
  if (outcome === 'skipped') {
    const conditionExplanation = getActionLogSkipConditionExplanation(log, t);
    if (conditionExplanation) return conditionExplanation;
  }
  if (/^Queued for reanalysis by \d+$/.test(rawReason) && outcome === 'waiting') {
    return t('actionsLogs.activity.explanations.reanalysisQueued', 'The request was received. The event is waiting to be analysed again.');
  }
  if (log.action_type === 'file_process' && processingSummary && outcome === 'failed') {
    // Only the existing, validated processing-error contract is suitable for the main view.
    try {
      const parsed = JSON.parse(rawReason);
      if (parsed?.code && processingSummary !== rawReason) return processingSummary;
    } catch {
      if (/^File processing ended with status (ERROR_AI|ERROR_AI_THROTTLE|ERROR_TIMEOUT|ERROR_CONTENT_FILTER|3|6|7|11)$/.test(rawReason)) {
        return processingSummary;
      }
    }
  }
  if (outcome === 'skipped' && /^(Conditions not met|Skipped due to unmet conditions)$/i.test(rawReason)) {
    return t('actionsLogs.activity.explanations.conditions', 'This action did not apply because its conditions were not met.');
  }
  if (outcome === 'skipped' && /^(?:Error details: )?Action was skipped \(conditions not met or file filtered\)$/m.test(rawReason)) {
    return t('actionsLogs.activity.explanations.filtered', 'The action did not run because its conditions or event selection rules were not met.');
  }
  const explanations = {
    done: 'The action finished successfully.',
    failed: 'The action could not be completed. Open the technical details for the recorded error; ask an administrator for help if needed.',
    skipped: 'This action was not carried out. The reason is not specified.',
    reused: 'A previous result was reused; the action was not run again.',
    started: 'The action was requested. This entry does not confirm that it finished.',
    running: 'The action was in progress when this entry was recorded.',
    waiting: 'The action was waiting to start when this entry was recorded.',
    unknown: 'The result of this action was not recorded in a recognised format.'
  };
  return t(`actionsLogs.activity.explanations.${outcome}`, explanations[outcome]);
};

export const ACTION_OUTCOMES = {
  done: ['Completed', 'success', 'The action finished successfully.'],
  failed: ['Failed', 'error', 'The action could not finish. Open Technical details to see the error.'],
  skipped: ['Skipped', 'warning', 'The action was not carried out. Check the entry for the recorded reason.'],
  reused: ['Previous result reused', 'info', 'A saved result was used. The action did not run again.'],
  started: ['Requested', 'info', 'The action was requested. This entry does not confirm that it started or finished.'],
  running: ['In progress', 'info', 'The action was running when this entry was saved.'],
  waiting: ['Waiting', 'default', 'The action was waiting to run when this entry was saved.'],
  unknown: ['Unknown result', 'default', 'No clear result was recorded for this action.']
};
